import TableIdButton from "@lib/components/MiniComponents/Buttons/TableIdButton";
import { shortenString } from "@lib/helpers/StringHelper";
import { TransactionType } from "@lib/types/Transaction";
import { ColumnsType } from "antd/es/table";
import { ColumnConfig, generateColumns } from "@lib/helpers/TableHelper";
import CoinPriceTextFormat from "@lib/components/MiniComponents/Others/CoinPriceTextFormat";
import CustomerName from "@lib/components/MiniComponents/Others/CustomerName";
import ProviderName from "@lib/components/MiniComponents/Others/ProviderName";
import { TableColumnsProps } from "@lib/types/General";
import { AuthRoles } from "@lib/types";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { Typography } from "antd";
import {
  PaymentHistoryState,
  PaymentHistoryStatus,
} from "@lib/components/MiniComponents/Others";

type Props<T> = TableColumnsProps<T> & {
  handleSelect: (item: T) => void;
  isMobile: boolean;
};

const getColumns = <T extends TransactionType>(
  props: Props<T>,
): ColumnsType<T> => {
  const { userRole, handleSelect, isMobile } = props;

  const columnsConfig: ColumnConfig<T>[] = [
    {
      title: "ID",
      dataIndex: "id",
      width: "130px",
      render: (v, r) => (
        <Typography.Text
          ellipsis
          copyable={{
            text: r.id,
          }}
        >
          {r.id}
        </Typography.Text>
      ),
      filterType: "search",
    },
    {
      title: "External ID",
      dataIndex: "externalId",
      width: "130px",
      render: (v, r) => (
        <TableIdButton
          text={`#${shortenString(r.externalId)}`}
          textToBeCopied={r.externalId}
        />
      ),
      filterType: "search",
    },
    {
      title: "common.amount",
      dataIndex: "amount",
      align: isMobile ? "left" : "right",
      width: "150px",
      render: (value, r) => (
        <CoinPriceTextFormat
          amount={value}
          showCurrency={r?.currency}
          showCurrencyImage
          align={isMobile ? "left" : "right"}
        />
      ),
      filterType: "search",
    },
    {
      title: "common.convertedAmount",
      dataIndex: "convertedAmount",
      width: "160px",
      align: isMobile ? "left" : "right",
      render: (value, r) => (
        <CoinPriceTextFormat
          amount={value}
          showCurrency={r?.convertedTo}
          align={isMobile ? "left" : "right"}
        />
      ),
    },
    {
      title: "common.status",
      dataIndex: "status",
      key: "paymentHistory.status",
      width: "120px",
      render: (status) => <PaymentHistoryStatus status={status} />,
    },
    {
      title: "common.state",
      dataIndex: "state",
      key: "paymentHistory.state",
      width: "160px",
      render: (state) => <PaymentHistoryState state={state} />,
    },
    {
      title: "common.provider",
      dataIndex: "provider.name",
      key: "provider.name",
      width: "200px",
      render: (v, r) => (
        <ProviderName provider={r?.provider} link size="small" />
      ),
      hiddenByRole: [
        AuthRoles.CustomerAgent,
        AuthRoles.CustomerManager,
        AuthRoles.ProviderAgent,
      ],
      userRole: userRole,
    },
    {
      title: "common.customer",
      dataIndex: "customer.id",
      key: "customer.name",
      width: "250px",
      render: (v, r) => (
        <CustomerName customer={r?.customer} link size="small" />
      ),
      hiddenByRole: [
        AuthRoles.CustomerAgent,
        AuthRoles.CustomerManager,
        AuthRoles.ProviderAgent,
      ],
      userRole: userRole,
    },
    {
      title: "common.updatedAt",
      dataIndex: "updatedAt",
      width: "170px",
    },
    {
      title: "common.createdAt",
      dataIndex: "createdAt",
      width: "170px",
    },
    {
      title: "common.createdBy",
      dataIndex: "createdBy",
      width: "170px",
      filterType: "dropdown",
      hiddenByRole: [
        AuthRoles.CustomerAgent,
        AuthRoles.CustomerManager,
        AuthRoles.ProviderAgent,
      ],
      userRole: userRole,
    },
    {
      title: "#",
      dataIndex: "actions",
      className: "order-table-action",
      fixed: "right",
      width: "40px",
      render: (_, record) => (
        <StyledFlex className="actions">
          <ActionButton
            type="detail"
            onClick={() => handleSelect(record)}
            noLabel
          />
        </StyledFlex>
      ),
    },
  ];

  return generateColumns(props, columnsConfig);
};

export default getColumns;
